import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { GeistMono } from "geist/font/mono";
import { useTheme } from "next-themes";

import type { SupportedLang } from "@acme/validators";

export function getMonacoLanguage(lang: SupportedLang) {
  switch (lang) {
    case "tsx":
      return "typescript";
    case "angular":
      return "html";
    default:
      return lang;
  }
}

export function getMonacoPath(lang: SupportedLang, name: string) {
  const prefix = `file:///example/${name}`;
  switch (lang) {
    case "angular":
      return `${prefix}.component.html`;
    case "tsx":
      return `${prefix}.tsx`;
    case "typescript":
      return `${prefix}.ts`;
    case "javascript":
      return `${prefix}.js`;
    case "yaml" as SupportedLang:
      return `${prefix}.yaml`;
    case "json" as SupportedLang:
      return `${prefix}.json`;
    case "python":
      return `${prefix}.py`;
    case "ruby" as SupportedLang:
      return `${prefix}.rb`;
    case "rust" as SupportedLang:
      return `${prefix}.rs`;
    case "swift" as SupportedLang:
      return `${prefix}.swift`;
    case "kotlin" as SupportedLang:
      return `${prefix}.kt`;
    case "php" as SupportedLang:
      return `${prefix}.php`;
    case "c_sharp" as SupportedLang:
      return `${prefix}.cs`;
    case "c" as SupportedLang:
      return `${prefix}.c`;
    case "cpp" as SupportedLang:
      return `${prefix}.cpp`;
    case "elixir" as SupportedLang:
      return `${prefix}.ex`;
    case "go" as SupportedLang:
      return `${prefix}.go`;

    default:
      return `${prefix}.${lang}`;
  }
}

export function injectJsAstGrepTypes(monaco: Monaco) {
  monaco.editor.onDidCreateModel((model) => {
    const uri = model.uri.toString();

    // Only apply to TypeScript files
    if (uri.endsWith(".ts") || uri.endsWith(".tsx")) {
      // Set up a content change listener
      model.onDidChangeContent(() => {
        const content = model.getValue();

        // Check if the file imports from ast-grep-wasm
        if (
          content.includes('from "@ast-grep/napi"') ||
          content.includes("from '@ast-grep/napi'")
        ) {
          // If it does, make sure the types are loaded
          monaco.languages.typescript.typescriptDefaults.addExtraLib(
            `// This is a proxy module that redirects to the actual type definitions
import * as AstGrepWasm from '@ast-grep/napi';
export = AstGrepWasm;
export as namespace AstGrepWasm;`,
            "file:///node_modules/@ast-grep/napi.d.ts",
          );
        }
      });
    }
  });
}

export const monacoEditorOptions = {
  minimap: { enabled: false },
  fontSize: 14,
  fontFamily: GeistMono.style.fontFamily,
  formatOnType: false,
  formatOnPaste: false,
  quickSuggestions: false,
  parameterHints: { enabled: false },
  suggestOnTriggerCharacters: false,
  acceptSuggestionOnEnter: "off",
  tabCompletion: "off",
  renderValidationDecorations: "off",
  smoothScrolling: true,
  scrollbar: {
    useShadows: false,
    verticalScrollbarSize: 7,
    horizontalScrollbarSize: 7,
    verticalSliderSize: 11,
    horizontalSliderSize: 11,
  },
  overviewRulerLanes: 0,
  codeLens: false,
  scrollBeyondLastLine: false,
  lightbulb: {
    enabled: "on" as editor.ShowLightbulbIconMode,
  },
} satisfies editor.IEditorOptions;

export const monacoEditorOptionsWithSyntaxValidation = {
  ...monacoEditorOptions,
  acceptSuggestionOnEnter: "on",
  tabCompletion: "on",
  renderValidationDecorations: "on",
  suggestOnTriggerCharacters: true,
  quickSuggestions: true, // enable automatic suggestions
} satisfies editor.IEditorOptions;

export const useMonacoEditorTheme = () => {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === "dark" ? "github-dark" : "github-light";
};
